import React, { useState } from 'react';
import { Button, Card, Col, Modal, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { I18NextNS } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useNavigateAfterConfirm from 'src/hooks/useNavigateAfterConfirm';
import useSubmitSession from 'src/hooks/useSubmitSession';

type TeleophthPatientConfirmationModalProps = {
  formValues: Partial<Session>;
  isShowConfirmModal: boolean;
  handleCloseConfirmModal: () => void;
  InfoConfirmationKeyMap: { [key in keyof Partial<Session>]?: string };
};

export default function TeleophthPatientConfirmationModal({
  formValues,
  isShowConfirmModal,
  handleCloseConfirmModal,
  InfoConfirmationKeyMap,
}: TeleophthPatientConfirmationModalProps) {
  const { t } = useTranslation(I18NextNS.COMMON);
  const { updateSession } = useRemoteBoundStore();
  const { initializeRetinalExam } = useSubmitSession();
  const navigateAfterConfirm = useNavigateAfterConfirm();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    updateSession(formValues);

    setIsConfirming(true);
    await initializeRetinalExam(formValues);
    setIsConfirming(false);
    navigateAfterConfirm();
  };

  return (
    <CustomModal
      show={isShowConfirmModal}
      onHide={handleCloseConfirmModal}
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t('patientFields.confirmPatientInformation')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <Stack gap={2}>
              {(
                ['dob', 'firstName', 'lastName', 'id'] as Array<
                  keyof Omit<Session, 'template'>
                >
              ).map((objKey) => (
                <Row key={objKey}>
                  <Col>
                    <span className="fw-medium">
                      {t(`patientFields.${InfoConfirmationKeyMap[objKey]}`)}
                    </span>
                  </Col>
                  <Col>
                    <span>{formValues[objKey]?.toString()}</span>
                  </Col>
                </Row>
              ))}
            </Stack>
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary-100"
          onClick={handleCloseConfirmModal}
          disabled={isConfirming}
        >
          {t('modal.cancel')}
        </Button>

        <Button
          variant="primary"
          className="shadow"
          onClick={handleConfirm}
          disabled={isConfirming}
        >
          {isConfirming ? t('modal.confirming') : t('modal.confirm')}
        </Button>
      </Modal.Footer>
    </CustomModal>
  );
}
